import React from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Stack,
  Icon,
  Button,
  Divider,
  VStack,
  HStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaBookReader, 
  FaUsers, 
  FaHandsHelping, 
  FaHandHoldingHeart,
  FaSeedling,
  FaHeartbeat,
  FaChalkboardTeacher,
  FaBullhorn,
  FaHome,
  FaHandshake,
  FaAppleAlt,
  FaBrain
} from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const Programs: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Define interface for program data
  interface ProgramArea {
    title: string;
    description: string;
    icon: IconType;
    color: string;
    image: string;
    initiatives: {
      title: string;
      description: string;
      icon: IconType;
    }[];
  }

  // Programs data
  const programAreas: ProgramArea[] = [
    {
      title: 'Education & Training',
      description: 'Building knowledge and skills through workshops, courses, and training programs.',
      icon: FaBookReader,
      color: 'brand.500',
      image: '/assets/images/photo_2025-05-04_15-30-06.jpg',
      initiatives: [
        {
          title: 'Literacy Programs',
          description: 'Basic reading and writing skills for adults and children',
          icon: FaChalkboardTeacher
        },
        {
          title: 'Vocational Training',
          description: 'Practical skills development for sustainable livelihoods',
          icon: FaBookReader
        },
        {
          title: 'Peace Education',
          description: 'Conflict resolution and peacebuilding skills',
          icon: FaUsers
        }
      ]
    },
    {
      title: 'Advocacy',
      description: 'Amplifying voices and advocating for policies that promote peace and justice.',
      icon: FaUsers,
      color: 'green.500',
      image: '/assets/images/photo_2025-05-04_15-30-12.jpg',
      initiatives: [
        {
          title: 'Policy Engagement',
          description: 'Working with local authorities on community-centered policies',
          icon: FaBullhorn
        },
        {
          title: 'Rights Awareness',
          description: 'Educating communities about their rights and responsibilities',
          icon: FaUsers
        },
        {
          title: 'Youth Advocacy',
          description: 'Empowering young people to advocate for their needs',
          icon: FaHandsHelping
        }
      ]
    },
    {
      title: 'Community Initiatives',
      description: 'Supporting local projects that address specific community needs.',
      icon: FaHandsHelping,
      color: 'earth.500',
      image: '/assets/images/photo_2025-05-04_15-30-19.jpg',
      initiatives: [
        {
          title: 'Community Gardens',
          description: 'Sustainable food production and nutrition improvement',
          icon: FaSeedling
        },
        {
          title: 'Housing Improvements',
          description: 'Safe and dignified housing solutions',
          icon: FaHome
        },
        {
          title: 'Clean Water Projects',
          description: 'Access to safe drinking water and sanitation',
          icon: FaHandsHelping
        }
      ]
    },
    {
      title: 'Partnerships',
      description: 'Collaborating with organizations to maximize impact and reach.',
      icon: FaHandHoldingHeart,
      color: 'brand.500',
      image: '/assets/images/photo_2025-05-04_15-30-26.jpg',
      initiatives: [
        {
          title: 'NGO Collaborations',
          description: 'Working with other organizations for greater impact',
          icon: FaHandshake
        },
        {
          title: 'Government Engagement',
          description: 'Partnering with local authorities for sustainable solutions',
          icon: FaHandHoldingHeart
        },
        {
          title: 'Private Sector Partnerships',
          description: 'Engaging businesses in community development',
          icon: FaHandshake
        }
      ]
    },
    {
      title: 'Resource Provision',
      description: 'Ensuring communities have access to essential resources for development.',
      icon: FaSeedling,
      color: 'green.500',
      image: '/assets/images/photo_2025-05-04_15-30-34.jpg',
      initiatives: [
        {
          title: 'Food Security',
          description: 'Ensuring reliable access to nutritious food',
          icon: FaAppleAlt
        },
        {
          title: 'Educational Materials',
          description: 'Books, supplies, and learning resources',
          icon: FaBookReader
        },
        {
          title: 'Emergency Relief',
          description: 'Rapid response to community crises',
          icon: FaHandsHelping
        }
      ]
    },
    {
      title: 'Mental & Emotional Wellbeing',
      description: 'Supporting holistic health through counseling and wellness programs.',
      icon: FaHeartbeat,
      color: 'earth.500',
      image: '/assets/images/photo_2025-05-04_15-30-38.jpg',
      initiatives: [
        {
          title: 'Trauma Counseling',
          description: 'Support for those affected by conflict and violence',
          icon: FaBrain
        },
        {
          title: 'Community Healing',
          description: 'Group-based approaches to collective trauma',
          icon: FaUsers
        },
        {
          title: 'Youth Mentorship',
          description: 'Supporting young people through positive role models',
          icon: FaHandHoldingHeart
        }
      ]
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-30-42.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            Our Programs
          </MotionHeading>
          
          <MotionText
            fontSize={{ base: 'lg', md: 'xl' }}
            mt={4}
            maxW="800px"
            mx="auto"
            initial="hidden"
            animate="visible"
            variants={slideUp}
          >
            Through Education, Dialogue, and Advocacy (EDA), we work to transform communities and create lasting positive change.
          </MotionText>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Key Activity Areas
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              ALPHA implements a variety of programs focused on empowering vulnerable and marginalized communities through six key activity areas.
            </Text>
          </MotionBox>
          
          {/* Program Areas */}
          {programAreas.map((program, index) => (
            <MotionBox
              key={index}
              mb={20}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerChildren}
            >
              <MotionFlex
                direction={{ base: 'column', lg: index % 2 === 0 ? 'row' : 'row-reverse' }}
                align="center"
                justify="space-between"
                variants={fadeIn}
              >
                <MotionBox
                  flex="1"
                  mr={{ base: 0, lg: index % 2 === 0 ? 10 : 0 }}
                  ml={{ base: 0, lg: index % 2 === 0 ? 0 : 10 }}
                  mb={{ base: 10, lg: 0 }}
                  variants={slideUp}
                >
                  <Image
                    src={program.image}
                    alt={program.title}
                    borderRadius="lg"
                    shadow="xl"
                    w="full"
                    h={{ base: '250px', md: '350px' }}
                    objectFit="cover"
                  />
                </MotionBox>
                
                <MotionBox flex="1" variants={slideUp}>
                  <HStack spacing={4} mb={4}>
                    <Flex
                      w="50px"
                      h="50px"
                      borderRadius="full"
                      bg={`${program.color}10`}
                      color={program.color}
                      justify="center"
                      align="center"
                    >
                      <ChakraIcon icon={program.icon} boxSize="24px" />
                    </Flex>
                    
                    <Heading
                      as="h3"
                      fontSize={{ base: 'xl', md: '2xl' }}
                      color={program.color}
                    >
                      {program.title}
                    </Heading>
                  </HStack>
                  
                  <Text fontSize={{ base: 'md', md: 'lg' }} mb={8}>
                    {program.description}
                  </Text>
                  
                  <Heading as="h4" fontSize="lg" mb={4}>
                    Key Initiatives:
                  </Heading>
                  
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
                    {program.initiatives.map((initiative, idx) => (
                      <Box
                        key={idx}
                        p={4}
                        borderRadius="md"
                        bg="gray.50"
                        sx={{ transition: 'all 0.3s ease' }}
                        _hover={{ transform: 'translateY(-5px)', shadow: 'md' }}
                      >
                        <VStack align="flex-start" spacing={2}>
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg={`${program.color}10`}
                            color={program.color}
                            justify="center"
                            align="center"
                            mb={2}
                          >
                            <ChakraIcon icon={initiative.icon} boxSize="20px" />
                          </Flex>
                          
                          <Heading as="h5" fontSize="md">
                            {initiative.title}
                          </Heading>
                          
                          <Text fontSize="sm" color="gray.600">
                            {initiative.description}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                </MotionBox>
              </MotionFlex>
            </MotionBox>
          ))}
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', md: 'row' }}
            align="center"
            justify="space-between"
            bg="white"
            p={{ base: 8, md: 12 }}
            borderRadius="lg"
            boxShadow="md"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, md: 8 }}
              mb={{ base: 6, md: 0 }}
              variants={slideUp}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Want to Support Our Programs?
              </Heading>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                Your contribution can help us expand our programs and reach more communities in need. Join us in creating positive change in the Democratic Republic of Congo.
              </Text>
            </MotionBox>
            
            <MotionBox variants={slideUp}>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
              >
                <Button
                  variant="primary"
                  size="lg"
                >
                  Donate Now
                </Button>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  size="lg"
                >
                  Become a Partner
                </Button>
              </Stack>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>
    </Box>
  );
};

export default Programs;
